import { isEnglishLanguage, resolveEnglishOnlyText } from '@/utils/statsig.utils';
import { useLayer, useStatsigClient } from '@statsig/react-bindings';
import useGlobalState from './useGlobalState.hooks';

export interface IStatsigLayer {
  // Raw read for non-text params such as image URLs; reads the layer on every page language
  get: (key: string, fallback?: string) => string;
  // Text read: Statsig text params hold English only, so the Statsig value is used on English pages only.
  // Non-English pages, an empty value or a not-ready client get the translated fallback without reading the layer
  getEnglishOnlyText: (key: string, fallback: string) => string;
  isReady: boolean;
  isEnglishPage: boolean;
  groupName: string | null;
}

export const useStatsigLayer = (layerName: string): IStatsigLayer => {
  const { client } = useStatsigClient();
  const layer = useLayer(layerName);
  const { pmtCurrentLanguageCode } = useGlobalState();
  const isReady = client.loadingStatus === 'Ready';
  const isEnglishPage = isEnglishLanguage(pmtCurrentLanguageCode);

  const get = (key: string, fallback?: string) => {
    if (!isReady) return fallback ?? '';
    return layer.get(key, fallback ?? '');
  };

  const getEnglishOnlyText = (key: string, fallback: string) =>
    resolveEnglishOnlyText({ isReady, isEnglishPage, readParam: () => layer.get(key, ''), fallback });

  return { get, getEnglishOnlyText, isReady, isEnglishPage, groupName: isReady ? layer.groupName : null };
};
